import React, { useEffect, useRef, useState } from "react";
import mermaid from "mermaid";
import styles from "./MermaidDiagram.module.css";

interface MermaidDiagramProps {
  chart: string;
}

export const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ chart }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isRendering, setIsRendering] = useState(false);

  useEffect(() => {
    mermaid.initialize({
      startOnLoad: false,
      theme: "default",
      securityLevel: "loose",
      suppressErrorRendering: true,
      fontFamily: `"IBM Plex Sans", Inter, system-ui, Avenir, Helvetica, Arial, sans-serif`,
    } as Parameters<typeof mermaid.initialize>[0]);
  }, []);

  useEffect(() => {
    const renderDiagram = async () => {
      if (!ref.current || !chart || isRendering) return;

      setIsRendering(true);
      try {
        // Generate a unique ID for this diagram
        const id = `mermaid-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

        // Use mermaid.render() to generate SVG
        const { svg } = await mermaid.render(id, chart);

        // Set the rendered SVG as innerHTML first so we can compute styles
        if (ref.current) {
          ref.current.innerHTML = svg;

          // Wait for next frame to ensure styles are computed and rendered
          requestAnimationFrame(() => {
            // Add a small delay to ensure SVG is fully processed
            setTimeout(() => {
              if (!ref.current) return;

              const svgElement = ref.current.querySelector("svg");
              if (!svgElement) return;

              // Inline computed styles into all elements for copy-paste support
              const inlineComputedStyles = (element: Element) => {
                // Get all computed styles
                const computedStyle = window.getComputedStyle(element);

                // Build inline style string from computed styles
                const inlineStyle: string[] = [];

                // Get all CSS properties (better than hardcoding)
                for (let i = 0; i < computedStyle.length; i++) {
                  const prop = computedStyle[i];
                  const value = computedStyle.getPropertyValue(prop);

                  // Only include properties that have meaningful values
                  if (
                    value &&
                    value !== "none" &&
                    value !== "rgba(0, 0, 0, 0)" &&
                    value !== "transparent" &&
                    value !== "auto" &&
                    value !== "normal" &&
                    !value.includes("matrix(1, 0, 0, 1, 0, 0)") // Default transform
                  ) {
                    // Convert camelCase to kebab-case for CSS properties
                    const kebabProp = prop
                      .replace(/([A-Z])/g, "-$1")
                      .toLowerCase();
                    inlineStyle.push(`${kebabProp}: ${value}`);
                  }
                }

                // Also explicitly get SVG-specific properties that might not be in computedStyle
                const svgSpecificProps = [
                  "fill",
                  "stroke",
                  "stroke-width",
                  "stroke-dasharray",
                  "stroke-linecap",
                  "stroke-linejoin",
                  "fill-opacity",
                  "stroke-opacity",
                ];

                svgSpecificProps.forEach((prop) => {
                  const value = computedStyle.getPropertyValue(prop);
                  if (
                    value &&
                    value !== "none" &&
                    value !== "rgba(0, 0, 0, 0)"
                  ) {
                    inlineStyle.push(`${prop}: ${value}`);
                  }
                });

                if (inlineStyle.length > 0) {
                  // Merge with existing style attribute
                  const existingStyle = element.getAttribute("style") || "";
                  const mergedStyle = existingStyle
                    ? `${existingStyle}; ${inlineStyle.join("; ")}`
                    : inlineStyle.join("; ");
                  element.setAttribute("style", mergedStyle);
                }

                // Recursively process children
                Array.from(element.children).forEach(inlineComputedStyles);
              };

              // Process all elements in the SVG
              inlineComputedStyles(svgElement);
            }, 100); // Small delay to ensure SVG is fully rendered
          });
        }
      } catch (error) {
        console.error("Error rendering mermaid diagram:", error);
        if (ref.current) {
          ref.current.innerHTML = `<pre>Error rendering diagram</pre>`;
        }
      } finally {
        setIsRendering(false);
      }
    };

    renderDiagram();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart]);

  return <div ref={ref} className={`mermaid ${styles.mermaid}`} />;
};
